import { useEffect, useRef, useState } from "react";
import {
  DetectTodayResult,
  GetMobilityLive,
  MobileInfo,
  MobilityReportByUser,
} from "../../types/types";
import styled from "@emotion/styled";
import theme from "../../theme";
import {
  calculateArrowInterval,
  handleKeyDown,
  textSubstring,
} from "../../utils/util";
import { CloseAndPrintButtons } from "./CloseAndPrintButtons";
import SubHeader from "../../screens/stastics/subHeader";
import { MobilityList } from "./MobilityList";
import useGetMobility from "../../hooks/useGetMobility";
import userStorage from "../../utils/userStorage";
import { CustomerInfo } from "./CustomerInfo";
import { OperationHistory } from "./OperationHistory";
import useGetMobilityReport from "../../hooks/useGetMobilityReport";
import { format } from "date-fns";
import { RouteMap } from "./RouteMap";
import useGetMobilityRoute from "../../hooks/useGetMobilityRoute";
import { ChartSection } from "./ChartSection";
import { useSnackbar } from "notistack";
import { getAddressFromCoords } from "../../api/geocoding";
import { DotLoader } from "../dotLoader";
import { useI18n } from "../../locales";

const { sm, md, lg } = theme.screen;

type Prop = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data?: GetMobilityLive | DetectTodayResult;
  date?: string;
  id: string;
  startDateData?: Date | null;
  endDateData?: Date | null;
  isPast?: boolean; // 과거 감지내역 조회에서 접근했는지 여부
};
export const MobilityUsageReportModal = ({
  open,
  setOpen,
  data,
  date,
  id, // header_seq
  startDateData,
  endDateData,
  isPast,
}: Prop) => {
  const user = userStorage.get();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useI18n();
  const printAreaComponentRef = useRef<HTMLDivElement>(null); // 프린트 영역
  const deviceListComponentRef = useRef<HTMLDivElement>(null); // 보유 기기 목록 스크롤 포커스용
  const reportListComponentRef = useRef<HTMLDivElement>(null); // 최근 운행내역 스크롤 포커스용
  const selectedReportListRef = useRef<HTMLDivElement>(null); // 선택된 리포트 리스트 포커스 이동
  const [isNotInitialValue, setIsNotInitialValue] = useState(false); // 과거 감지내역에서 접근했을 때 초기값인지 아닌지 확인
  const [fakeStartDate, setFakeStartDate] = useState<Date | null>(
    new Date()
  );
  const [fakeEndDate, setFakeEndDate] = useState<Date | null>(new Date());
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [selectedMobility, setSelectedMobility] =
    useState<MobileInfo | null>(null); // 선택된 보유 기기 목록 데이터
  const [selectedReport, setSelectedReport] =
    useState<MobilityReportByUser | null>(null); // 선택된 운행내역 데이터
  const [noReportData, setNoReportData] = useState(false); // 팝업메시지용
  const [arrowInterval, setArrowInterval] = useState(10); // 경로 데이터 불러올때 화살표 찍히는 간격 설정하는 파라미터
  const [center, setCenter] = useState({
    lat: Number(user?.latitude),
    lng: Number(user?.longitude),
  });
  const [mapLevel, setMapLevel] = useState(13);
  const [mobilityPage, setMobilityPage] = useState(0);
  const [reportPage, setReportPage] = useState(0);
  const [currentLocation, setCurrentLocation] = useState("");

  // 사용자의 현재 위치 구하기
  useEffect(() => {
    if (data) {
      const getRoad = async () => {
        try {
          const address = await getAddressFromCoords(data.latitude, data.longitude);
          setCurrentLocation(address);
        } catch (error) {
          console.error(t.control.addressLookupError, error);
          setCurrentLocation(t.control.currentLocationError);
        }
      };
      getRoad();
    }
  }, [data, selectedMobility, selectedReport]);

  // 날짜 초기화
  useEffect(() => {
    if (!isPast && date) {
      setStartDate(new Date(date));
      setEndDate(new Date(date));
    }
  }, [date]);

  // 과거 감지내역 페이지 날짜 초기화
  useEffect(() => {
    if (isPast && startDateData) {
      setFakeStartDate(startDateData);
      setStartDate(startDateData);
    }
    if (isPast && endDateData) {
      setFakeEndDate(endDateData);
      setEndDate(endDateData);
    }
  }, [startDateData, isPast]);

  // 보유 기기 목록 데이터
  const { data: mobilityData, isLoading: mobilityDataIsLoading } =
    useGetMobility({
      limit: 10, // -1 전체
      page: mobilityPage,
      searchKey: data?.name || data?.owner,
      fetch: true,
      mobilityCode: -1,
      area: user?.area as string,
    });

  // 보유 기기 목록 전체 데이터
  const { data: mobilityDataAll } = useGetMobility({
    limit: -1, // -1 전체
    page: 0,
    searchKey: data?.name || data?.owner,
    fetch: true,
    mobilityCode: -1,
    area: user?.area as string,
  });

  // 최근 운행 이력 데이터
  const { data: reportData, isLoading: reportDataIsLoading } =
    useGetMobilityReport({
      limit: 10,
      page: reportPage,
      startDate: format(new Date(startDate as Date), "yyyy-MM-dd"),
      endDate: format(new Date(endDate as Date), "yyyy-MM-dd"),
      mobility_seq: selectedMobility?.seq as number,
      open,
      fetch: true,
    });

  // 최근 운행 이력 데이터
  const { data: reportDataAll, isLoading: reportDataAllIsLoading } =
    useGetMobilityReport({
      limit: -1,
      page: 0,
      startDate: format(new Date(startDate as Date), "yyyy-MM-dd"),
      endDate: format(new Date(endDate as Date), "yyyy-MM-dd"),
      mobility_seq: selectedMobility?.seq as number,
      open,
      fetch: true,
    });

  // 최근 운행 이력 경로 데이터
  const { data: routeData, isLoading: routeDataIsLoading } =
    useGetMobilityRoute({
      second: arrowInterval, // 10
      device_pid: selectedReport?.device_pid as string,
      date: format(new Date(startDate as Date), "yyyy-MM-dd"),
      seq: selectedReport?.running_seq as string,
      open,
    });

  const changeStartDate = (date: Date | null) => {
    setFakeStartDate(date);
  };

  const changeEndDate = (date: Date | null) => {
    setFakeEndDate(date);
  };

  // 보유 기기 목록 클릭
  const handleClickMobilityList = (el: MobileInfo) => {
    setIsNotInitialValue(true); // 과거 감지내역 초기값 용
    setSelectedReport(null);

    setSelectedMobility(el);
    setCenter({
      lat: Number(user?.latitude),
      lng: Number(user?.longitude),
    });
    setMapLevel(13);
    setReportPage(0);
  };

  // 최근 운행내역 클릭
  const handleClickReportList = (el: MobilityReportByUser) => {
    setIsNotInitialValue(true); // 과거 감지내역 초기값 용
    setSelectedReport(el);
    calculateArrowInterval(el.cumulative_distance, setArrowInterval);
  };

  // 과거감지내역이 아니거나, 첫번째 데이터일때 - 보유기기목록
  useEffect(() => {
    if ((!isPast || isNotInitialValue) && mobilityDataAll) {
      setSelectedMobility(
        mobilityDataAll.list.find(
          (el) => el.device_pid === data?.device_pid
        ) ?? mobilityDataAll.list[0]
      );
    }
  }, [id, mobilityDataAll]);

  // 과거감지내역이 아니거나, 첫번째 데이터일때 - 최근운행내역
  useEffect(() => {
    if (
      (!isPast || isNotInitialValue) &&
      reportData &&
      reportData.list.length > 0
    ) {
      setSelectedReport(reportData.list[0]);
      setMapLevel(13);
      setCenter({
        lat: Number(user?.latitude),
        lng: Number(user?.longitude),
      });
      if (reportData.list.length > 0) {
        calculateArrowInterval(
          reportData.list[0].cumulative_distance,
          setArrowInterval
        );
      }
    }

    if (!isPast && !reportData) {
      setSelectedReport(null);
      setMapLevel(13);
      setCenter({
        lat: Number(user?.latitude),
        lng: Number(user?.longitude),
      });
    }
  }, [reportData]);

  // 과거감지내역 첫번째 데이터 설정 - 보유 기기 목록
  useEffect(() => {
    if (
      isPast &&
      data &&
      !isNotInitialValue &&
      !selectedMobility &&
      mobilityDataAll &&
      mobilityDataAll.list.length > 0 &&
      Object.keys(data).includes("code_name") &&
      Object.keys(data).includes("running_seq")
    ) {
      setSelectedMobility(
        mobilityDataAll.list.filter(
          (el) => el.device_pid == data.device_pid
        )[0]
      );
    }
  }, [data, mobilityDataAll, isNotInitialValue, selectedReportListRef]);

  // 과거감지내역 첫번째 데이터 설정 - 최근 운행 이력
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    if (
      isPast &&
      data &&
      !isNotInitialValue &&
      selectedMobility &&
      reportDataAll &&
      reportDataAll.list.length > 0
    ) {
      const targetIndex = reportDataAll.list.findIndex(
        (el) => el.running_seq == data.running_seq
      );
      // let initialData = reportDataAll.list.filter(
      //   (el) => el.running_seq == data.running_seq
      // );
      // setSelectedReport(initialData[0]);
      setSelectedReport(reportDataAll.list[targetIndex]);

      // 페이지 이동
      if (targetIndex > -1) {
        // 선택된 데이터 인덱스가 페이지 데이터 수보다 작음 (맨 앞 페이지임)
        if (targetIndex <= 10) {
          setReportPage(0);
        }
        // 선택된 데이터 인덱스가 페이지 데이터 수 보다 많음 (페이지 이동)
        else {
          const value = Math.ceil(targetIndex / 10);
          setReportPage(value - 1);
        }
      }

      // 일치되는 데이터가 없는 경우 팝업 메시지 출력 - 조회된 데이터가 있는데 선택된 데이터가 없음
      if (
        !reportDataAllIsLoading &&
        reportDataAll.list.length > 0 &&
        // initialData.length === 0
        targetIndex < 0
      ) {
        setNoReportData(true);
        timeoutId = setTimeout(() => {
          setNoReportData(false);
        }, 1000);
      }
    }
    return () => timeoutId && clearTimeout(timeoutId);
  }, [selectedMobility, reportDataAll, reportDataAllIsLoading]);

  // 데이터 선택하면 스크롤 이동 - 첫 화면에서만 이동
  useEffect(() => {
    if (isPast && !isNotInitialValue && selectedReport) {
      selectedReportListRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedReport, selectedReportListRef]);

  // 농기계 사고 관제 날짜 초기화
  useEffect(() => {
    // 운행중인데 당일 운행내역이 없는 경우, 조회 시작일을 운행시작일로 변경
    if (isPast || !selectedMobility) return;
    if (selectedMobility.status === "운행중") {
      if (reportData?.list.length === 0) {
        const runningStartDate = selectedMobility.min_registered_at;
        setFakeStartDate(new Date(runningStartDate));
        setStartDate(new Date(runningStartDate));
        enqueueSnackbar(t.control.deviceOperationStartDateMessage, {
          variant: "info",
        });
      }
    }
  }, [reportData, selectedMobility]);

  // 테스트 코드
  // const dayDiff = (startDate: Date, endDate: Date) => {
  //   if (differenceInDays(endDate as Date, startDate as Date) > 1) {
  //     window.alert("하루만 조회가 가능합니다.");
  //     return true;
  //   }
  //   return false;
  // };

  // console.log("selectedMobility", selectedMobility);
  // console.log("selectedReport", selectedReport);
  // console.log("reportData", reportData);
  // console.log("routeData", routeData);
  // console.log("data.running_seq", data?.running_seq);
  // console.log("mobilityData", mobilityData?.list);
  // console.log("mobilityDataAll", mobilityDataAll?.list);
  // console.log("data(전달받은 데이터)", data);
  // console.log("reportDataAll", reportDataAll);

  return (
    <WrapperStyledDiv
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        let check = window.confirm(t.control.closeReportConfirm);
        if (!check) return;
        setOpen(false);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {/* 모바일에서만 사용되는 로더 */}
      {reportDataAllIsLoading && (
        <LoaderWrapper>
          <DotLoader width="60px" height="12px" />
        </LoaderWrapper>
      )}
      <div style={{ position: "relative" }}>
        {/* 닫기, 프린트 버튼 */}
        <CloseAndPrintButtons
          onClose={() => setOpen(false)}
          printAreaComponentRef={printAreaComponentRef}
        />
        {/* 프린트 영역 */}
        <InnerWrapperStyledDiv open={open} ref={printAreaComponentRef}>
          {/* 타이틀 */}
          <TitleStyledH1>
            {t.control.usageReportTitle(
              textSubstring(
                data?.name ?? data?.owner,
                0,
                50
              )
            )}
          </TitleStyledH1>

          <SubHeader
            date={date}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            changeEndDate={changeEndDate}
            changeStartDate={changeStartDate}
            fakeStartDate={fakeStartDate}
            fakeEndDate={fakeEndDate}
            isReportModal={true}
            setIsNotInitialValue={setIsNotInitialValue}
            // dayDiffCheck={dayDiff}
          />

          <TopRowWrapperStyledDiv>
            <MobilityList
              data={data}
              mobilityData={mobilityData}
              mobilityDataIsLoading={mobilityDataIsLoading}
              selectedMobility={selectedMobility}
              handleClickMobilityList={handleClickMobilityList}
              listRef={deviceListComponentRef}
              mobilityPage={mobilityPage}
              setMobilityPage={setMobilityPage}
            />
            <CustomerInfo data={data} currentLocation={currentLocation} />
          </TopRowWrapperStyledDiv>

          <OperationHistory
            listRef={reportListComponentRef}
            selectedRef={selectedReportListRef}
            reportData={reportData}
            reportDataIsLoading={reportDataIsLoading}
            selectedReport={selectedReport}
            handleClickReportList={handleClickReportList}
            noData={noReportData}
            reportPage={reportPage}
            setReportPage={setReportPage}
            currentLocation={currentLocation}
          >
            <RouteMap
              routeData={routeData}
              routeDataIsLoading={routeDataIsLoading}
              center={center}
              setCenter={setCenter}
              mapLevel={mapLevel}
              setMapLevel={setMapLevel}
              selectedReport={selectedReport}
              reportData={reportData?.list}
              isPast={isPast}
              isNotInitialValue={isNotInitialValue}
              setNoReportData={setNoReportData}
            />
          </OperationHistory>

          <ChartSection
            startDate={startDate}
            endDate={endDate}
            ownerID={data?.phone}
            area={user?.area as string}
            mobility_seq={selectedMobility?.seq as number}
          />
        </InnerWrapperStyledDiv>
      </div>
    </WrapperStyledDiv>
  );
};

const WrapperStyledDiv = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 500ms;
  background: rgba(0, 0, 0, 0.3);
  z-index: ${theme.zindex.customModal}; /* 98 - 사이드바(31)보다 위에 표시 */

  @page {
    size: A3 portrait; // 세로
    margin: 5mm;

    /* margin: 0mm; */
    /* padding: 30mm; */
  }
  @media print {
    width: 210mm;
    height: 297mm;
    background: ${theme.color.white};
  }

  @media (min-width: ${lg + 1}px) {
    bottom: 0;
  }
`;

const InnerWrapperStyledDiv = styled.div<{ open: boolean }>`
  position: relative;
  background-color: ${theme.color.white};
  padding-top: 15px;
  padding-left: 60px;
  padding-right: 60px;
  overflow-y: scroll;
  border-radius: 20px;
  padding-bottom: 15px;
  transition: 200ms;
  opacity: ${({ open }) => (open ? 1 : 0)};
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: ${md}px) {
    width: 100vw;
    height: 100vh;
    padding-left: 10px;
    padding-right: 10px;
    padding-top: 50px;
    padding-bottom: 150px;
    border-radius: 0px;
  }

  @media (min-width: ${sm + 1}px) and (max-width: ${md}px) {
    width: 100vw;
    height: 100vh;
    padding-left: 15px;
    padding-right: 15px;
  }
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    width: 90vw;
    min-width: 700px;
    height: 100vh;
  }
  @media (min-width: ${lg + 1}px) {
    min-width: 1200px;
    height: 90vh;
  }
  @media print {
    width: 1300px;
    height: 90vh;
  }
`;

const TitleStyledH1 = styled.h1`
  min-height: 40px;
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 18px;
  line-height: 21px;
  color: ${theme.color.neutral_500};
  margin-bottom: 5px;
  padding-right: 20px;

  @media (max-width: ${md}px) {
    height: 45px;
    width: 95vw;
    position: fixed;
    top: 0px;
    background-color: ${theme.color.white};
    z-index: ${theme.zindex.customModal + 1};
  }
`;

const TopRowWrapperStyledDiv = styled.div`
  margin-top: 15px;
  display: flex;
  gap: 30px;
  margin-bottom: 20px;

  @media (max-width: ${lg}px) {
    flex-direction: column;
    gap: 10px;
  }

  @media print {
    flex-direction: row;
  }
`;

const LoaderWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: ${theme.color.neutral_400}55;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  @media (min-width: ${md + 1}px) {
    display: none;
  }
`;
